import { useState, useRef, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { aboutHeroImage, aboutTeamImage } from '@/mocks/images';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9sqdtt8o1vn02ekukag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '健康性知识科普平台',
    url: 'https://your-domain.com/',
    description: '致力于提供安全、科学、健康的性教育知识与资讯，帮助用户理性看待成人内容，保护个人隐私与网络安全。',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-123-4567',
      contactType: 'customer service',
      email: 'contact@health-edu.cn',
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 健康性知识科普平台',
    description: '了解健康性知识科普平台的品牌故事、专业团队与联系方式',
    url: 'https://your-domain.com/about',
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 品牌介绍与联系方式"
        description="健康性知识科普平台致力于提供安全、科学、健康的性教育知识与资讯。了解我们的品牌故事、专业团队与联系信息。"
        keywords="啪啪啪在线观看,成人内容安全须知,健康性知识平台,联系我们,品牌介绍"
        canonicalPath="/about"
        schema={{ ...orgSchema, ...aboutSchema }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="relative w-full h-[320px] md:h-[420px] overflow-hidden">
          <img
            src={aboutHeroImage}
            alt="关于健康性知识科普平台"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/35"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-3">
                关于我们
              </h1>
              <p className="text-white/80 text-sm md:text-base max-w-lg">
                致力于提供安全、科学、健康的性教育知识与资讯
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800 mb-4">
                品牌故事
              </h2>
              <p className="text-foreground-500 leading-relaxed max-w-2xl mx-auto">
                健康性知识科普平台成立于2024年，由一群关注公共卫生健康事业的专业人士共同创建。
                我们深知在互联网时代，人们获取性教育知识的渠道良莠不齐，错误的信息可能带来严重的后果。
                因此，我们致力于打造一个安全、科学、健康的性教育科普平台。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mb-10">
              {[
                {
                  icon: 'ri-eye-line',
                  title: '我们的愿景',
                  desc: '成为国内领先的科学性教育科普平台，让每个人都能获取安全健康的性知识。',
                },
                {
                  icon: 'ri-flag-line',
                  title: '我们的使命',
                  desc: '传播科学的性教育知识，帮助用户建立正确的性观念，保护个人隐私与网络安全。',
                },
                {
                  icon: 'ri-heart-line',
                  title: '核心价值观',
                  desc: '科学、严谨、关爱、责任——以科学精神传播知识，以真诚关爱服务用户。',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="p-6 bg-background-50 rounded-lg border border-background-200/60 text-center"
                >
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                    <i className={`${item.icon} text-xl text-primary-500`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-800 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800 mb-4">
                专业团队
              </h2>
              <p className="text-foreground-500 max-w-2xl mx-auto">
                我们的团队成员来自医学、心理学、法律和网络安全等多个专业领域
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center mb-10">
              <div className="rounded-lg overflow-hidden bg-background-100">
                <img
                  src={aboutTeamImage}
                  alt="健康性知识科普平台专业团队"
                  className="w-full aspect-[16/10] object-cover"
                />
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold text-foreground-800 mb-4">
                  跨学科专家团队
                </h3>
                <p className="text-foreground-500 leading-relaxed mb-4">
                  我们的内容由医学专家、心理学顾问、法律专业人士和网络安全工程师共同审核把关。
                  每一篇文章都经过严格的事实核查和专业审查，确保信息的准确性和可靠性。
                </p>
                <ul className="space-y-3">
                  {[
                    '医学专家团队负责审核健康知识的科学性',
                    '心理学顾问确保心理健康内容的专业性',
                    '法律专家提供法规解读的专业支持',
                    '网络安全工程师保障平台的技术安全',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-foreground-600">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-accent-500"></i>
                      </div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800 mb-4">
                平台信誉
              </h2>
              <p className="text-foreground-500 max-w-2xl mx-auto">
                我们始终坚持内容的科学性和严谨性，赢得了用户的广泛信赖
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
              {[
                { value: '500+', label: '科普文章' },
                { value: '50万+', label: '月访问用户' },
                { value: '98%', label: '用户好评率' },
                { value: '4.9', label: '内容评分' },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="p-6 bg-background-50 rounded-lg border border-background-200/60 text-center"
                >
                  <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800 mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500">
                如您有任何问题、建议或合作意向，欢迎通过以下方式联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              {[
                { icon: 'ri-mail-line', title: '电子邮箱', value: 'contact@health-edu.cn' },
                { icon: 'ri-phone-line', title: '客服电话', value: '400-123-4567' },
                { icon: 'ri-map-pin-line', title: '办公地址', value: '中国北京市朝阳区' },
              ].map((item) => (
                <div
                  key={item.title}
                  className="p-4 bg-background-50 rounded-lg border border-background-200/60 text-center"
                >
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                    <i className={`${item.icon} text-lg text-primary-500`}></i>
                  </div>
                  <h4 className="text-xs text-foreground-400 mb-1">{item.title}</h4>
                  <p className="text-sm font-medium text-foreground-700">{item.value}</p>
                </div>
              ))}
            </div>

            <form
              ref={formRef}
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-background-50 rounded-lg border border-background-200/60 p-6"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1">
                    姓名
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-3 py-2 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1">
                    邮箱
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-3 py-2 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-3 py-2 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入消息主题"
                />
              </div>
              <div className="mb-4">
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1">
                  留言内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={4}
                  maxLength={500}
                  className="w-full px-3 py-2 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                ></textarea>
              </div>
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-honeypot"
              />
              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'submitting' ? '提交中...' : '发送消息'}
              </button>

              {formStatus === 'success' && (
                <p className="mt-3 text-sm text-secondary-600 text-center">
                  您的消息已成功发送，我们会尽快与您联系！
                </p>
              )}
              {formStatus === 'error' && formError && (
                <p className="mt-3 text-sm text-red-500 text-center">{formError}</p>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}